export type TransferProvider = 'jira' | 'trello' | 'standalone';

export interface TransferStatusMappingEntry {
  sourceStatus: string;
  targetStatus: string;
}

export type TransferItemStatus = 'creating' | 'created' | 'failed';

export interface TransferItem {
  id: string; // stable local id
  sourceId: string; // provider-native id of the source task/issue/card
  title: string;
  description?: string;
  sourceStatus: string; // raw status/list name as read from source
  targetStatus: string; // resolved from job.statusMapping
  dueDate?: string; // always absent when source is Jira (not synced)
  transferStatus?: TransferItemStatus; // undefined until the orchestrator first touches it
  transferError?: string;
  targetId?: string; // created target task/issue/card id, once known
}

export type TransferJobStatus =
  | 'reading'
  | 'mapping_proposed'
  | 'mapping_confirmed'
  | 'transferring'
  | 'completed'
  | 'failed';

export interface TransferJob {
  _id: string;
  userId: string;
  status: TransferJobStatus;
  sourceProvider: TransferProvider;
  targetProvider: TransferProvider;
  sourceProjectId: string; // internal synced project/board id (jira/trello) or standalone project id
  sourceProjectName?: string; // filled once reading finishes
  targetProjectName: string;
  statusMapping: TransferStatusMappingEntry[];
  items: TransferItem[];
  targetContainerId?: string; // Jira project key / Trello board id / standalone project id
  targetListIdByStatus?: Record<string, string>; // Trello-target only
  readError?: string;
  mappingError?: string; // non-fatal — LLM call failed/invalid, heuristic fallback used
  transferError?: string;
  createdAt: number;
  updatedAt: number;
}

// Target status vocabulary is constrained for two of the three providers (validate client-side
// before submitting a mapping edit); Trello targets are freeform list names.
export const TARGET_STATUS_OPTIONS: Record<TransferProvider, string[] | null> = {
  standalone: ['todo', 'in_progress', 'done'],
  jira: ['To Do', 'In Progress', 'Done'],
  trello: null,
};

export const TRANSFER_PROVIDER_LABEL: Record<TransferProvider, string> = {
  jira: 'Jira',
  trello: 'Trello',
  standalone: 'Standalone',
};
